// Chat RUNTIME personnage↔joueur autour d'une US (CCT-74).
//
// Ouvert par le push du moteur quand le joueur se déclare BLOQUÉ (hint du personnage owner),
// quand sa livraison est REFUSÉE (explication + indice) ou VALIDÉE (acceptation).
//
// L'UI est ALIGNÉE sur celle du chat scénarisé (MissionChatScreen) — CCT-97 : mêmes avatars,
// nom d'expéditeur, bulles et panneau de choix, pour que le joueur vive UNE seule expérience de
// conversation. Seule la donnée diffère (fil du moteur vs storyline authorée).
//
// Le joueur ne répond que par les options servies par le serveur : « J'ai compris » (ferme le
// fil) et, selon le cas, « Pouvez-vous m'expliquer davantage ? ». Pas de champ libre.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Ionicons from 'react-native-vector-icons/Ionicons';

import ActionLoader from '../components/ActionLoader';
import ChatThread from '../components/ChatThread';

import { getUsChat, listAllUserStories, listUsChats, replyUsChat,
         BoardUserStory, UsChat, UsChatSummary } from '../api/userStories';
import { setOpenView } from '../push/openView';
import { sameRoute } from '../utils/notificationRoute';
import { getMissionPlayer } from '../data/scenarios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';
import { TYPE_COLOR } from '../utils/cyb';
import { sameElement } from '../utils/elementId';
import ElementHeader from '../components/ElementHeader';
import { useT } from '../i18n/useT';
import { elementKey } from '../utils/elementId';
import { useUpdates } from '../context/UpdatesContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { apiFetch } from '../api/client';
import { getScenarioForMission, loadRuntimeScenario } from '../data/scenarios';
import { buildNavSequence, useItemNav, type SeqItem } from '../utils/itemNav';
import type { MessageKey } from '../i18n';

const ACCENT = TYPE_COLOR.chat;   // même accent que le chat scénarisé
// Même repli d'avatar joueur que MissionChatScreen (expérience identique).
// CCT-253 — plus d'avatar de repli BUNDLÉ : le joueur parle en son nom, et à défaut de
// photo de profil l'initiale colorée vaut mieux qu'un visage emprunté à une AUTRE histoire.
const PLAYER_AVATAR_FALLBACK = undefined;

// ⚠️ KEYS, not translated strings — a module-level map is built before any hook has run.
// Shared wording with the chat list (ChatScreen): one catalogue entry, two screens.
const SUBTITLE: Record<string, MessageKey> = {
  refused: 'chat.tone.refused',
  validated: 'chat.tone.validated',
  blocked: 'chat.tone.blocked',
  // The player has just spoken and nobody has answered yet (CCT-345). It is a TONE like the
  // others — what the thread is about right now — not a separate conversation.
  handover: 'chat.tone.handover',
};

const hhmm = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function UsChatScreen({ navigation, route, onProgressConfirmed }: any) {
  const t = useT();
  const { getAccessToken } = useAuth();
  const { isDarkTheme } = useTheme();
  const { playerName, playerAvatar } = usePlayer();
  const colors = getThemeColors(isDarkTheme);
  // CCT-152 — c'est le JOUEUR qui parle, pas le personnage qu'il incarne : on affiche SON nom
  // et SON avatar (profil). Le personnage désigné (`getMissionPlayer`) sert à savoir QUI il
  // incarne dans l'histoire, il n'usurpe pas son identité dans la conversation.
  const playerAvatarImg = playerAvatar ?? PLAYER_AVATAR_FALLBACK;
  const missionId: number = Number(route?.params?.missionId);
  const elementId: string = String(route?.params?.elementId ?? route?.params?.itemId ?? '');

  const [chat, setChat] = useState<UsChat | null>(null);
  // The task the thread is about. It starts at the route's, and a chip changes it — CCT-352.
  const [openElement, setOpenElement] = useState<string>(elementId);
  useEffect(() => { setOpenElement(elementId); }, [elementId]);
  // The character's OTHER conversations in this mission, and the tasks' identifiers. A thread
  // knows one task; the filter needs to know the neighbours.
  const [siblings, setSiblings] = useState<UsChatSummary[]>([]);
  const [tasks, setTasks] = useState<BoardUserStory[]>([]);
  const [busy, setBusy] = useState(true);
  // Même règle que le centre de messagerie : la réplique du joueur s'affiche tout de suite,
  // la réflexion du personnage se lit SOUS elle, à la place de sa réponse.
  const [pendingChoice, setPendingChoice] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { loadProgress } = useStoryProgress();
  const [freshSequence, setFreshSequence] = useState<SeqItem[] | null>(null);
  const [refreshingProgress, setRefreshingProgress] = useState(false);
  const [progressError, setProgressError] = useState(false);
  const refreshedVerdict = useRef<string | null>(null);
  const itemNav = useItemNav({ params: {
    ...route?.params, missionId, itemId: openElement,
    itemSeq: freshSequence ?? [],
    itemIndex: freshSequence?.findIndex(i => sameElement(i.id, openElement)) ?? -1,
  } }, navigation, openElement);
  const scrollRef = useRef<ScrollView>(null);

  const refreshValidatedProgress = useCallback(async () => {
    setRefreshingProgress(true);
    setProgressError(false);
    setFreshSequence(null);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Session unavailable');
      // Read the controller's decision; never complete a task on behalf of the engine.
      await loadRuntimeScenario(missionId,
        () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }), true);
      await loadProgress(missionId);
      await onProgressConfirmed?.();
      setFreshSequence(buildNavSequence(getScenarioForMission(missionId)?.chapters ?? []));
    } catch {
      setProgressError(true);
    } finally {
      setRefreshingProgress(false);
    }
  }, [missionId, getAccessToken, loadProgress, onProgressConfirmed]);

  useEffect(() => {
    refreshedVerdict.current = null;
    setFreshSequence(null);
    setProgressError(false);
  }, [missionId, openElement]);
  useEffect(() => {
    // Works for polling, push and an already validated thread reopened later.
    if (!['validated', 'ready'].includes(chat?.reason ?? '') || !chat || !sameElement(chat.element_id, openElement)) return;
    const verdict = [...chat.turns].reverse().find(turn => turn.role === 'character');
    if (!verdict) return;
    const key = `${missionId}/${openElement}/${verdict.at}`;
    if (refreshedVerdict.current === key) return;
    refreshedVerdict.current = key;
    void refreshValidatedProgress();
  }, [chat, missionId, openElement, refreshValidatedProgress]);

  const load = useCallback(async () => {
    try {
      setChat(await getUsChat(getAccessToken(), missionId, openElement));
    } catch (e: any) {
      setError(t('usChat.none'));
    } finally { setBusy(false); setPendingChoice(null); }
  }, [missionId, openElement]);

  useEffect(() => { void load(); }, [load]);
  // ⚠️ THE FILTER NEEDS THE NEIGHBOURS. This screen is served ONE thread; the tasks of the same
  // character in the same mission come from the message list, their identifiers from the board.
  // Both are already loaded elsewhere in the app — here they cost one call each, on opening.
  useEffect(() => {
    void (async () => {
      try {
        const [c, u] = await Promise.all([listUsChats(getAccessToken()),
                                          listAllUserStories(getAccessToken())]);
        setSiblings((c.chats ?? []).filter(x => x.missionId === missionId));
        setTasks((u.userStories ?? []).filter(x => x.mission_id === missionId));
      } catch { /* no filter, and the conversation stays perfectly readable */ }
    })();
  }, [missionId, getAccessToken]);

  // Reading a conversation is what clears the message badge (CCT-350): seeing the
  // list is not reading it, and a notification opens straight here.
  const { markSeen } = useUpdates();
  useEffect(() => { markSeen('chat'); }, [markSeen]);


  // CCT-345 — THE THREAD IS ALIVE WHILE IT IS OPEN, AND IT STOPS DUPLICATING ITSELF.
  //
  // The engine answers ASYNCHRONOUSLY: the player hands the task over, their own sentence is
  // already in the thread, and the character's reply lands seconds later. Two things follow, and
  // they are the same fact seen from either side:
  //   * this screen must RELOAD when that push arrives — otherwise the reply the player is
  //     waiting for, staring at the conversation, only appears if they navigate away and back;
  //   * the browser must NOT pop a notification for a message being read as it arrives. We
  //     declare the displayed route to the service worker, which then skips the popup while this
  //     tab is visible — the push itself is still delivered (badge, centre, this reload).
  //
  // Leaving the screen clears the declaration: a route left registered would silence
  // notifications for a conversation nobody is looking at any more.
  useEffect(() => {
    const route = `/us-chat/${missionId}/${elementId}`;
    setOpenView(route);
    const nav: any = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
    const onSwMsg = (ev: MessageEvent) => {
      const d: any = ev.data || {};
      if (d.type !== 'cc-push' && d.type !== 'cc-push-open') return;
      // A push carrying ANOTHER route (a reminder on a different task) must not reload this
      // thread. With no route at all we reload: an unknown push is more likely to concern the
      // conversation being read than not, and a spurious reload costs one request.
      if (d.route && !sameRoute(d.route, route)) return;
      void load();
    };
    nav?.serviceWorker?.addEventListener?.('message', onSwMsg);
    return () => {
      setOpenView(null);
      nav?.serviceWorker?.removeEventListener?.('message', onSwMsg);
    };
  }, [missionId, elementId, load]);

  const answer = async (choice: string) => {
    setPendingChoice(options.find((o: any) => o.id === choice)?.label ?? '');
    setBusy(true); setError('');
    try {
      const next = await replyUsChat(getAccessToken(), missionId, openElement, choice);
      setChat(next);
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 60);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally { setBusy(false); setPendingChoice(null); }
  };

  // FILTER BY USER STORY — CCT-352.
  //
  // ⚠️ THE SAME IDENTIFIER THE CHARACTER SAYS (`us_key`, e.g. `SIM-100-1`): the sentence right
  // above reads "j'ai passé la user story SIM-100-1…". A chip labelled otherwise would name the
  // same task twice, in two vocabularies.
  //
  // ⚠️ AND THE TWO FORMS OF AN ID MEET HERE TOO: the threads carry `el-107600`, the board
  // `107600`. Compared raw they never match — and a filter that never matches shows nothing.
  const taskOf = (eid: any) => tasks.find(u => String(u.element_id) === elementKey(eid)) ?? null;
  const otherTasks = siblings
    .filter(s => chat?.character_name == null || s.characterName === chat.character_name)
    .map(s => ({ elementId: s.elementId, task: taskOf(s.elementId) }))
    .filter((x, i, arr) => arr.findIndex(y => elementKey(y.elementId) === elementKey(x.elementId)) === i);

  const last = chat?.turns?.[chat.turns.length - 1];
  const options = !chat?.closed && last?.role === 'character' ? last.replies : [];
  // The player has spoken and nobody has answered yet — the engine is working behind this screen.
  const awaitingAnswer = !!chat && !chat.closed && last?.role === 'student' && !pendingChoice;

  // CCT-345 — ⚠️ THE PUSH IS NOT A GUARANTEE, AND WITHOUT THIS THE ANSWER NEVER LANDS. The thread
  // reloads on the service worker's message, but web push needs a permission the player may have
  // refused, may not have been asked for, or that the browser does not support at all. In that
  // case the character's answer sat in the database while the screen showed "waiting" for ever.
  //
  // ⚠️ BOUNDED, AND ONLY WHILE WAITING. Three seconds apart, twenty attempts at most (a minute),
  // and only while the last turn is the player's own — never a background poll. The measured
  // precedent is `/me/notifications`, whose 45-second poll alone kept the container app awake
  // 53 % of the time (CCT-233): a poll that outlives what it waits for is a cost with no reader.
  // A review the server says is PENDING (journal-based cloud story: one to two minutes of real
  // Activity Log lag) is worth a longer bounded wait — the character is announced as typing,
  // and the verdict must land on this screen, not after a navigation.
  const pending = chat?.pending ?? null;
  useEffect(() => {
    if (!awaitingAnswer) return;
    let attempts = 0;
    let stopped = false;
    const limit = pending ? 70 : 20;
    const timer = setInterval(() => {
      if (stopped || ++attempts > limit) { clearInterval(timer); return; }
      void load();
    }, 3000);
    return () => { stopped = true; clearInterval(timer); };
  }, [awaitingAnswer, pending, load]);
  const avatar = chat?.avatarkey && chat.avatarkey.startsWith('http') ? chat.avatarkey : null;
  const name = chat?.character_name || 'Message';
  // ⚠️ THE ELEMENT IS THE TASK BEING DISCUSSED, not the character. The thread belongs to a user
  // story; naming the character on that line would repeat the avatar and lose the one thing the
  // player cannot see from here. The thread's reason is the fallback, never an empty line.
  const titreElement = tasks.find((x) => sameElement(x.element_id, openElement))?.title
    || (SUBTITLE[chat?.reason ?? ''] ? t(SUBTITLE[chat?.reason ?? '']) : name);

  const Avatar = () => (
    avatar
      ? <Image source={{ uri: avatar }} style={st.msgAvatarImage} resizeMode="cover" />
      : <View style={[st.msgAvatar, { backgroundColor: ACCENT }]}>
          <Text style={st.msgAvatarText}>{name.slice(0, 1).toUpperCase()}</Text>
        </View>
  );

  return (
    <SafeAreaView style={[st.root, { backgroundColor: colors.background }]}>
      {/* ⚠️ THE SAME HEADER AS EVERY OTHER ELEMENT (CCT-390). This one named the CHARACTER and
          nothing else: a player arriving from a task lost the mission, the chapter, and the very
          task they had just handed over. The avatar stays, uniformity must not impoverish, and
          the character's name already sits above each of their bubbles. */}
      <ElementHeader
        missionTitle={route?.params?.missionTitle ?? ''}
        chapterTitle={route?.params?.chapterTitle ?? ''}
        chapterSubtitle={route?.params?.chapterSubtitle ?? ''}
        elementTitle={titreElement}
        onBack={() => navigation?.goBack?.()}
        leading={<View style={st.headerAvatar}><Avatar /></View>}
        badge={chat?.closed ? (
          <View style={[st.badge, { borderColor: colors.success, backgroundColor: `${colors.success}1F` }]}>
            <Text style={[st.badgeText, { color: colors.success }]}>{t('usChat.done')}</Text>
          </View>
        ) : null}
      />

      {/* One chip per task this character has a conversation about, in this mission. Below two,
          there is nothing to filter — a lone chip is noise. */}
      {otherTasks.length > 1 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={st.filterBar} contentContainerStyle={st.filterRow}>
          {otherTasks.map(({ elementId: eid, task }) => {
            const on = elementKey(eid) === elementKey(openElement);
            return (
              <TouchableOpacity key={String(eid)} activeOpacity={0.75}
                onPress={() => setOpenElement(String(eid))}
                style={[st.chip, {
                  backgroundColor: on ? colors.primary : colors.surfaceSecondary,
                  borderColor: on ? colors.primary : colors.border,
                }]}>
                <Text numberOfLines={1}
                  style={[st.chipText, { color: on ? colors.badgeText : colors.textSecondary }]}>
                  {task?.us_key || task?.title || String(eid)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      {busy && !chat ? (
        <View style={st.center}><ActionLoader label={t('usChat.loading')} /></View>
      ) : error && !chat ? (
        <View style={st.center}>
          <Ionicons name="chatbubble-ellipses-outline" size={40} color={colors.textTertiary} />
          <Text style={[st.dim, { color: colors.textTertiary }]}>{error}</Text>
        </View>
      ) : (
        <>
          {/* Rendu délégué au moteur de chat UNIQUE (CCT-99) : ce fil et celui du personnage
              divergeaient visuellement alors qu'ils racontent la même conversation. */}
          <ChatThread
            mode="us"
            scrollRef={scrollRef}
            messages={[
              ...(chat?.turns ?? []).map((t: any) => ({ role: t.role, text: t.text, at: t.at })),
              ...(pendingChoice ? [{ role: 'student' as const, text: pendingChoice }] : []),
            ]}
            characterName={name}
            characterAvatar={avatar}
            playerAvatar={playerAvatarImg}
            playerName={playerName ?? null}
            options={options}
            busy={busy}
            // The character is composing: shown while a reply is being fetched AND while the
            // engine is answering a handover — otherwise the player faces their own sentence with
            // nothing indicating that anything is happening.
            awaiting={(busy && !!chat) || awaitingAnswer}
            onChoose={answer}
          />
          {options.length === 0 && (
            <Text style={[st.dim, { color: colors.textTertiary }]}>
              {/* "Analysis in progress" replaces the generic wait while the engine works; it
                  disappears with the character's answer, as the server stops sending it. */}
              {awaitingAnswer && pending
                ? (t(pending.messageKey as any) || pending.message)
                : chat?.closed && chat.reason === 'ready' ? t('us.status.READY') : t(chat?.closed
                  ? (chat.reason === 'validated' ? 'usChat.validated' : 'usChat.resume')
                  : 'usChat.waiting')}
            </Text>
          )}
          {(chat?.reason === 'validated' || chat?.reason === 'ready') && (
            <View style={{ padding: 12 }}>
              {refreshingProgress && <ActionLoader label={t('chapter.loading')} />}
              {progressError && (
                <TouchableOpacity accessibilityRole="button" onPress={refreshValidatedProgress}>
                  <Text style={{ color: colors.primary, textAlign: 'center' }}>{t('common.retry')}</Text>
                </TouchableOpacity>
              )}
              {chat.closed && freshSequence && !refreshingProgress && !progressError && (
                <TouchableOpacity accessibilityRole="button" onPress={itemNav.goNext}
                  style={{ padding: 14, borderRadius: 12, backgroundColor: ACCENT }}>
                  <Text style={{ color: '#FFFFFF', textAlign: 'center', fontWeight: '700' }}>
                    {t(itemNav.isLast ? 'usChat.backToChapter' : 'usChat.continue')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          {!!error && chat && <Text style={[st.dim, { color: colors.error }]}>{error}</Text>}
        </>
      )}
    </SafeAreaView>
  );
}

// Styles repris de MissionChatScreen (mêmes métriques) pour une expérience identique.
const st = StyleSheet.create({
  root: { flex: 1, minHeight: 0 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
  // `flexGrow: 0`: without it a horizontal ScrollView takes the column's flex and the bar
  // stretches over the whole height (the same trap as the board).
  filterBar: { flexGrow: 0, flexShrink: 0 },
  filterRow: { gap: 8, paddingHorizontal: 12, paddingVertical: 8, alignItems: 'center' },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6, maxWidth: 220 },
  chipText: { fontSize: 12, fontWeight: '600' },
  headerAvatar: { width: 32, height: 32, borderRadius: 16, overflow: 'hidden' },
  badge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1 },
  badgeText: { fontSize: 11, fontWeight: '700' },
  scroll: { flex: 1, minHeight: 0 },
  content: { padding: 12 },
  msgAvatarImage: { width: 32, height: 32, borderRadius: 16 },
  msgAvatar: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  msgAvatarText: { fontSize: 11, fontWeight: 'bold', color: '#FFFFFF' },
  dim: { fontSize: 12, textAlign: 'center', marginTop: 8 },
});
